// @ts-strict-ignore
import {ContextPanelMenuReactProps} from 'common/widgets/context-panel/context-panel-menu';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSharing} from 'common/widgets/sharing';
import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {showMetricsApiErrorToast, updateMetric} from 'common/report/queries/app-api/metrics';
import {ActionType, ContextPanelData, DetailType} from 'common/widgets/context-panel/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {ModelStatus} from 'common/util/model-resource/model-resource';
import {getEditCapability, getShareCapability} from 'common/report/util/saved-entities';
import {MetricsResultsMap} from 'app_api/projects/metrics/types';
import {isCanvasEnabled} from 'common/report/util/feature-sampling';
import {CurrentProject} from 'common/report/model-resources/projects';

import type {ContextPanelRendererParams} from '../child-menu';

/**
 * Provides handlers for events inside a metric context panel
 */
export const createContextPanelEventHandlers = (
  params: Partial<ContextPanelRendererParams> = {},
): Partial<ContextPanelMenuReactProps> => {
  // handles actions from, the context panel
  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const metricId = ev.detail.item.id;

    if (action === ActionType.ShareFormula) {
      await promptSharing({
        type: SharableEntityType.Metric,
        id: metricId,
        'project-id': Number(getProjectId()),
      });
    } else if (ev.detail.item.url && action === ActionType.ViewEntity) {
      window.open(ev.detail.item.url, `_blank`);
    } else {
      throw new Error(`action handler not implemented`);
    }
  };

  const handleContextPanelEditDescription = async ({detail}: CustomEvent) => {
    const metricId = detail.item.id;
    const updatedDescription = detail.description;

    let results: MetricsResultsMap;
    try {
      results = await updateMetric({
        metricId,
        metric: {
          description: updatedDescription,
        },
      });
    } catch {
      results = null;
      showMetricsApiErrorToast();
    }

    if (results) {
      params.availableContexts.metrics.update(results, {partialUpdate: false});
    }
  };

  return {
    onContextPanelHoverIn: params.handleHoverIn,
    onContextPanelHoverOut: params.handleHoverOut,
    onTooltipContextAction: handleContextPanelAction,
    onTooltipContextEditDescription: handleContextPanelEditDescription,
  };
};

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateFormulaContextPanelData({
  id,
  metricsContext,
  currentProject,
  canWriteMetrics = false,
  canShare = false,
}: {
  id: number;
  metricsContext: MetricsResource;
  currentProject: CurrentProject;
  canWriteMetrics?: boolean;
  canShare?: boolean;
}): ContextPanelData {
  const model = metricsContext.getOne(id);
  const metric = model.value;

  if (model.status === ModelStatus.Loading || !metric) {
    return null;
  }

  const permissions = getEditCapability(metric);
  const canvasEnabled = isCanvasEnabled(currentProject);
  const actions: ContextPanelData[`actions`] = [];
  const links: ContextPanelData[`links`] = [];
  const details: ContextPanelData[`details`] = [];

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: ActionType.ShareFormula,
      label: ({t}) => t(`query-builder.context-menu.share`),
      icon: `invite`,
      // Only superadmin or editor can share
      disabled: !getShareCapability(metric, EntityType.Metric).enableShare,
      disabledTooltipText: ({t}) => t(`query-builder.context-menu.share-no-access`),
      item: {
        id: metric.id,
        superadminOverrideShare: getShareCapability(metric).requireSuperadminOverride,
      },
    });
  }

  // view entity in lexicon link
  links.push({
    icon: `share`,
    href:
      getReportUrl(`lexicon`, {projectId: getProjectId(), workspaceId: getWorkspaceId()}) +
      `/transformations/formulas/${metric.id}`,
    tooltipText: ({t}) => t(`query-builder.context-menu.view-in-lexicon`),
  });

  actions.push({
    name: ActionType.ViewEntity,
    label: ({t}) => t(`query-builder.context-menu.view-in-lexicon`),
    icon: `share`,
    item: {
      id: metric.id,
      name: metric.name,
      url:
        getReportUrl(`lexicon`, {projectId: getProjectId(), workspaceId: getWorkspaceId()}) +
        `/transformations/formulas/${metric.id}`,
    },
  });

  if (metric.created_by) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.creator`),
      type: DetailType.User,
      value: {...metric.created_by},
    });
  }

  if (canvasEnabled && metric.owned_by) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.owner`),
      type: DetailType.User,
      value: {...metric.owned_by},
    });
  }

  return {
    actions,
    links,
    header: metric.name,
    subHeader: ({t}) => t(`query-builder.metrics.formula`),
    entityIcon: `formula`,
    description: metric.description,
    entityType: EntityType.Metric,
    excludedMetricIcons: [`pie`, `pulse`],
    details,
    // allows for editing the description of the metric
    editItem:
      canWriteMetrics && permissions.enableEdit
        ? {
            id: metric.id,
            type: EntityType.Metric,
            name: metric.name,
          }
        : null,
    verified: metric.verified,
    verifiedAt: metric.last_verified,
    verifiedBy: metric.last_verified_by?.name || metric.last_verified_by?.email,
  };
}
